import { api } from "../api";
import type { OpDay, OpList, QueueCat } from "../types";
import { nowMsk } from "../docs/helpers";

/** Оперирующий список — ФЭК и «Прочие операции»: со своими операционными днями. */
export const isOperatingList = (l: OpList | null | undefined) =>
  !!l && !!(l.writes_off_iol || l.free_oper_type) && !l.waiting_list;

/**
 * Список без дат — резерв, очередь ОМС, отказники: строки идут сплошным
 * списком, без группировки по опердню. Лист ожидания (ПРЕМИАЛЬНЫЕ) — свой вид.
 */
export const isHoldingList = (l: OpList | null | undefined) =>
  !!l && !isOperatingList(l) && !l.waiting_list;

/**
 * Имя списка с его разделом, если в разделе он не один: «ПЛАТНЫЕ · НЕ
 * ПРЕМИАЛЬНЫЕ». РЕЗЕРВ, названный как свой раздел, — просто «РЕЗЕРВ».
 */
export const listTitle = (l: OpList) => (l.section && l.section !== l.name ? `${l.section} · ${l.name}` : l.name);

/** В категории ещё есть место. */
export const catHasRoom = (c: QueueCat) => c.taken < c.slots;

/** Что сказать, когда в очереди некуда поставить пациента. */
export function noRoomText(list: OpList, cats: QueueCat[]): string {
  if (!cats.length) return `В «${list.name}» нет ни одной категории — сначала откройте категорию в самом списке`;
  return `В «${list.name}» все категории заполнены — свободных мест нет`;
}

function todayIso(): string {
  const d = nowMsk();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Что нужно, чтобы перенести строку в этот список:
 *   - в оперирующий — открытый опердень (только предстоящие, новых не заводят);
 *   - в очередь ОМС — категория со свободным местом;
 *   - в остальные — ничего.
 */
export type MoveNeed =
  | { kind: "none" }
  | { kind: "day"; days: OpDay[] }
  | { kind: "cat"; cats: QueueCat[] };

export async function moveNeed(target: OpList): Promise<MoveNeed> {
  if (isOperatingList(target)) {
    const today = todayIso();
    const res = await api.opDays(target.list_id);
    return { kind: "day", days: res.days.filter((d) => d.op_date.slice(0, 10) >= today) };
  }
  if (target.queue_cats) {
    const res = await api.queueCats(target.list_id);
    return { kind: "cat", cats: res.cats };
  }
  return { kind: "none" };
}
